import { Injectable, inject } from "@angular/core";
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client";
import { AppConfigService } from "@app/shared/services/app-config.service";
import { AuthenticationConfiguration } from "./authentication-configuration";

@Injectable({
    providedIn: "root",
})
export class JwtExternalAuthenticationService {
    private userManager: UserManager;
    private userPromise: Promise<User>;
    private user: User;
    private authenticationConfig: AuthenticationConfiguration;

    private appConfigService = inject(AppConfigService);

    constructor() {
        this.authenticationConfig = this.appConfigService.authenticationConfig;
        if (!this.authenticationConfig.jwtExternalLoginEnabled) {
            return;
        }

        const baseUrl = window.location.origin;

        const pkce = this.authenticationConfig.jwtUsePkceFlow;
        const redirectUri = pkce
            ? `${baseUrl}/auth/callback`
            : `${baseUrl}/bundles/assets/oidc-login-redirect.html`;

        const config: UserManagerSettings = {
            authority: this.authenticationConfig.jwtExternalAuthority,
            client_id: this.authenticationConfig.jwtExternalClientId,
            metadataUrl: this.authenticationConfig.jwtExternalMetadataUrl,
            redirect_uri: redirectUri,
            silent_redirect_uri: redirectUri,
            scope: "openid profile",
            post_logout_redirect_uri: baseUrl,
            loadUserInfo: false,
            prompt: "select_account",
            userStore: new WebStorageStateStore({ store: window.localStorage }),

            // Based on configuration we use implicit grant or pkce flow.
            response_type: pkce ? "code" : "id_token token",
            ...(!pkce && { extraQueryParams: { resource: this.authenticationConfig.jwtExternalClientId } }),
        };

        this.userManager = new UserManager(config);
        this.userPromise = this.loadUser();

        this.userManager.events.addUserLoaded(() => {
            this.loadUser();
        });
    }

    public awaitInitialization(): Promise<any> {
        return this.userPromise;
    }

    public login(): Promise<User> {
        return this.userManager.signinPopup();
    }

    public loginSilent(): Promise<User> {
        return this.userManager.signinSilent();
    }

    public logout(): Promise<any> {
        return this.userManager.signoutRedirect();
    }

    public removeUser(): Promise<void> {
        return this.userManager.removeUser();
    }

    public getExternalAuthenticationToken(): string {
        if (!this.user) {
            return null;
        }

        if (this.authenticationConfig.jwtUseIdToken) {
            return this.user.id_token;
        }

        return this.user.access_token;
    }

    private async loadUser(): Promise<User> {
        return this.userManager.getUser().then((user) => {
            if (user && !user.expired) {
                this.user = user;
            }

            return user;
        });
    }
}
